import { FEATURE_FLAGS, TRACKER_EVENTS } from '@/constants';
import { useAppContext } from '@/context/AppContext';
import { useFloatingWidgetContext } from '@/context/FloatingWidgetContext';
import useDuration from '@/hooks/useDuration';
import { EMessageType } from '@/types';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './FloatingWidget.module.css';
import { track } from '@/utils';
import { useLocation } from 'react-router-dom';

export interface IFloatingVariantProps {
  onTriggerClick: () => void;
  isOpen: boolean;
  isHidden: boolean;
}

export const FLOATING_WIDGET_Z_INDEX = 49;

const FloatingWidget = () => {
  const { xPlumToken, userConfig, isWebView } = useAppContext();

  const {
    isFloatingWidgetOpen,
    setIsFloatingWidgetOpen,
    isFloatingWidgetHidden,
    floatingVariantConfig,
    iframeRef,
    postMessage,
    readyEvents,
  } = useFloatingWidgetContext();

  const location = useLocation();

  const isTokenEventReady = readyEvents?.has(EMessageType.TOKEN);

  useEffect(() => {
    window.dev.i(
      'SENDING TOKEN...: ',
      Boolean(xPlumToken && isTokenEventReady && userConfig?.id)
    );
    if (xPlumToken && isTokenEventReady && userConfig?.id) {
      postMessage(EMessageType.TOKEN, xPlumToken);
    }
  }, [isTokenEventReady, xPlumToken, userConfig?.id]);

  const { startTimer, pauseTimer } = useDuration();

  useEffect(() => {
    // for tracking the open
    // supports both: open by clicking AND open through query params
    if (isFloatingWidgetOpen) {
      trackSupportToggle(isFloatingWidgetOpen);
    }
  }, [isFloatingWidgetOpen]);

  const trackSupportToggle = (isOpen: boolean) => {
    let duration = null;

    if (isOpen) {
      startTimer();
    } else {
      duration = pauseTimer();
    }

    const trackingPayload = {
      duration,
      variant: floatingVariantConfig?.variant,
      url: location.pathname,
    };

    track({
      event: isOpen ? TRACKER_EVENTS.FW_OPENED : TRACKER_EVENTS.FW_CLOSED,
      data: trackingPayload,
    });
  };

  const handleTriggerClick = () => {
    trackSupportToggle(!isFloatingWidgetOpen);
    setIsFloatingWidgetOpen(!isFloatingWidgetOpen);
  };

  const { TriggerComponent, iframeUrl, visibleInWebView, variant } =
    floatingVariantConfig ?? {};

  // useEffect(() => {
  //   if (iframeRef?.current && iframeUrl) {
  //     const url = new URL(iframeUrl);
  //     if (isWebView) {
  //       url.searchParams.append('webview', 'true');
  //     }
  //     iframeRef.current?.contentWindow?.location?.replace(url);
  //   }
  // }, [iframeUrl, isWebView]);

  const isCashlessBotEnabled =
    userConfig?.featureFlags?.find?.((feat) => {
      return feat.name === FEATURE_FLAGS.CASHLESS_BOT;
    })?.enabled ?? false;

  if (
    (!visibleInWebView && isWebView) ||
    (!isCashlessBotEnabled && variant === 'claims-bot') ||
    TriggerComponent == null
  ) {
    return null;
  }

  return (
    <>
      <TriggerComponent
        onTriggerClick={handleTriggerClick}
        isHidden={isFloatingWidgetHidden || isWebView}
        isOpen={isFloatingWidgetOpen}
      />
      {createPortal(
        <iframe
          ref={iframeRef}
          allowFullScreen
          loading="lazy"
          id="floating-widget"
          style={{
            zIndex: FLOATING_WIDGET_Z_INDEX,
          }}
          className={[
            styles.iframe,
            isFloatingWidgetOpen ? styles.iframeVisible : '',
            isWebView ? styles.webview : '',
          ].join(' ')}
          src={
            iframeUrl
              ? (() => {
                  const url = new URL(iframeUrl);
                  if (isWebView) {
                    url.searchParams.append('webview', 'true');
                  }
                  return url.toString();
                })()
              : ''
          }
        />,
        document.body
      )}
    </>
  );
};

export default FloatingWidget;
